<template>
  <div class="flex flex-col gap-1">
    <!-- Item with nested sub-sections -->
    <router-link
      v-if="hasChildren && routeWithQuery"
      :to="routeWithQuery"
      custom
      v-slot="{ navigate, href, isActive }"
    >
      <a
        :href="href"
        @click="(e) => handleParentNavigation(e, navigate)"
        :class="[
          isCollapsed
            ? 'dropdown-item group cursor-pointer'
            : 'group cursor-pointer',
          'block w-full',
        ]"
        style="text-decoration: none; color: inherit"
      >
        <SidebarItem
          :option="item"
          :is-expanded="true"
          :is-selected="isSelected"
          :show-arrow="true"
          :is-open="isOpen"
          :hideIcon="true"
          :app-id="item.appId"
          :app-name="item.appName"
          :dropdown-placement="dropdownPlacement"
          :always-visible-dropdown="alwaysVisibleDropdown"
          :class="isCollapsed ? 'min-w-40 p-2' : ''"
          @dropdown-open-change="(open) => handleDropdownClick(open, $event)"
          @dropdown-mode-change="
            (mode) => $emit('dropdown-mode-change', mode, item.id)
          "
        />
      </a>
    </router-link>

    <!-- Item with children but no route (fallback) -->
    <div
      v-else-if="hasChildren"
      :class="
        isCollapsed
          ? 'dropdown-item group cursor-pointer'
          : 'group cursor-pointer'
      "
      @click="toggleItem"
    >
      <SidebarItem
        :option="item"
        :is-expanded="true"
        :is-selected="isSelected"
        :show-arrow="true"
        :is-open="isOpen"
        :hideIcon="true"
        :app-id="item.appId"
        :app-name="item.appName"
        :dropdown-placement="dropdownPlacement"
        :always-visible-dropdown="alwaysVisibleDropdown"
        :class="isCollapsed ? 'min-w-40 p-2' : ''"
        @dropdown-open-change="
          (open) => $emit('dropdown-open-change', open, item.id)
        "
        @dropdown-mode-change="
          (mode) => $emit('dropdown-mode-change', mode, item.id)
        "
      />
    </div>

    <!-- Nested children -->
    <transition
      v-if="hasChildren"
      @enter="startEnterTransition"
      @leave="startLeaveTransition"
      @after-enter="endTransition"
      @after-leave="endTransition"
    >
      <div
        v-show="isOpen"
        :class="
          isCollapsed
            ? 'ml-4 flex flex-col gap-1 mt-1'
            : 'flex flex-col gap-1 pl-6'
        "
      >
        <SubSectionItem
          v-for="child in item.sub_sections"
          :key="child.id"
          :item="child"
          :depth="depth + 1"
          :is-collapsed="isCollapsed"
          :option-id="optionId"
          :parent-route="routeWithQuery || parentRoute"
          :dropdown-placement="dropdownPlacement"
          :always-visible-dropdown="alwaysVisibleDropdown"
          @dropdown-open-change="$emit('dropdown-open-change', $event)"
          @dropdown-mode-change="$emit('dropdown-mode-change', $event)"
          @item-click="$emit('item-click', $event)"
        />
      </div>
    </transition>

    <!-- Leaf item without children -->
    <router-link
      v-if="!hasChildren && routeWithQuery"
      :to="routeWithQuery"
      custom
      v-slot="{ navigate, href, isActive }"
    >
      <a
        :href="href"
        @click="(e) => handleNavigation(e, navigate)"
        :class="[isCollapsed ? 'dropdown-item' : 'group', 'block w-full']"
        style="text-decoration: none; color: inherit"
      >
        <SidebarItem
          :option="item"
          :is-expanded="true"
          :is-selected="isSelected"
          :hideIcon="true"
          :app-id="item.appId"
          :app-name="item.appName"
          :dropdown-placement="dropdownPlacement"
          :always-visible-dropdown="alwaysVisibleDropdown"
          :class="isCollapsed ? 'min-w-40 p-2' : ''"
          @dropdown-open-change="(open) => handleDropdownClick(open, $event)"
          @dropdown-mode-change="
            (mode) => $emit('dropdown-mode-change', mode, item.id)
          "
        />
      </a>
    </router-link>

    <!-- Leaf item without route -->
    <div
      v-else-if="!hasChildren"
      :class="isCollapsed ? 'dropdown-item' : 'group'"
    >
      <SidebarItem
        :option="item"
        :is-expanded="true"
        :is-selected="isSelected"
        :hideIcon="true"
        :is-disabled="true"
        :app-id="item.appId"
        :app-name="item.appName"
        :dropdown-placement="dropdownPlacement"
        :always-visible-dropdown="alwaysVisibleDropdown"
        :class="isCollapsed ? 'min-w-40 p-2' : ''"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, inject } from "vue";
import { RouterLink, useRouter } from "vue-router";
import SidebarItem from "./SidebarItem.vue";
import { useTransition } from "@/src/composables/useTransition";

const props = defineProps({
  item: { type: Object, required: true },
  depth: { type: Number, default: 0 },
  isCollapsed: { type: Boolean, default: false },
  optionId: { type: String, default: null },
  dropdownPlacement: { type: String, default: "bottomLeft" },
  alwaysVisibleDropdown: { type: Boolean, default: false },
  parentRoute: { type: [String, Object], default: null }, // Add parent route prop
});

const emit = defineEmits([
  "dropdown-open-change",
  "dropdown-mode-change",
  "item-click",
]);

const router = useRouter();
const { startEnterTransition, startLeaveTransition, endTransition } =
  useTransition();

// Inject shared functions from parent
const toggleNestedSubSection = inject("toggleNestedSubSection");
const getNestedSubSectionState = inject("getNestedSubSectionState");
const isOptionSelected = inject("isOptionSelected");

const itemKey = computed(() => `${props.item.id}-${props.depth}`);
const hasChildren = computed(() => props.item?.sub_sections?.length > 0);
const isOpen = computed(() => getNestedSubSectionState(itemKey.value));
const isSelected = computed(() => isOptionSelected(props.item));

const routeWithQuery = computed(() => {
  const baseRoute = props.item.route || props.parentRoute;

  if (!baseRoute) {
    return null;
  }

  const isCrmRoute =
    typeof baseRoute === "string"
      ? baseRoute.includes("/crm/")
      : baseRoute.path?.includes("/crm/");

  if (!isCrmRoute) {
    return typeof baseRoute === "string" ? baseRoute : baseRoute;
  }

  const subObjectValue = props.item.id;

  if (typeof baseRoute === "string") {
    return {
      path: baseRoute,
      query: { sub_object: subObjectValue },
    };
  }
  return {
    ...baseRoute,
    query: {
      ...baseRoute?.query,
      sub_object: subObjectValue,
    },
  };
});

const toggleItem = () => {
  toggleNestedSubSection(itemKey.value);
};

const handleItemClick = (e) => {
  emit("item-click", props.optionId);
};

const handleDropdownClick = (open, event) => {
  // Stop propagation to prevent navigation when dropdown opens
  if (event) {
    event.stopPropagation();
  }
  emit("dropdown-open-change", open, props.item.id);
};

const handleParentNavigation = (e, navigate) => {
  // Check if clicking on the arrow icon specifically (only the arrow image)
  const clickedElement = e.target;
  const isArrowImage =
    clickedElement.tagName === "IMG" &&
    clickedElement.alt === "right_arrow_small";
  const arrowContainer = clickedElement.closest(".size-4.transition-transform");
  const isArrowClick =
    isArrowImage || (arrowContainer && arrowContainer.contains(clickedElement));

  if (isArrowClick) {
    e.preventDefault();
    e.stopPropagation();
    toggleItem();
    return;
  }

  if (!props.isCollapsed || !hasChildren.value) {
    handleItemClick(e);
  }
  toggleItem();
  navigate(e);
};

const handleNavigation = (e, navigate) => {
  const isDropdownClick =
    e.target.closest(".ant-dropdown-trigger") ||
    e.target.closest("[data-dropdown-trigger]") ||
    e.target.classList.contains("ant-dropdown-trigger");

  if (isDropdownClick) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  if (!props.isCollapsed || !hasChildren.value) {
    handleItemClick(e);
  }
  navigate(e);
};
</script>
